import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import {
  Field,
  TextInput,
  TextArea,
  SelectInput,
  Toggle,
  FormActions,
  CollapsibleSection,
  ReorderButtons,
  CTAGroup,
  SectionDivider,
} from "../../components/FormField";
import { MediaImageField } from "../../components/MediaPickerDialog";
import { showSuccess, showError, showSeoWarnings } from "../../lib/toast";
import { moveItem } from "../templates/shared";
import { generateUUID } from "../../commonjquery/CommonJquery";
import {
  server_post_data,
  get_service_page_data,
  service_page_save_update,
} from "../../serviceconnection/ServiceConnection";
import { SEOSection } from "../../components/SEOSection";
import { ICON_OPTIONS } from "../../lib/iconsData";
const CATEGORIES = [
  { value: "integration-&-automation", label: "Integration & Automation" },
  { value: "implementation", label: "Implementation" },
  { value: "strategic-advisory", label: "Strategic Advisory" },
  { value: "support-&-managed-services", label: "Support & Managed Services" },
];

// ─── Reusable plain bullet repeater (string array) ────────────────────────
const StringRepeater = ({ items, setItems, placeholder, addBtn, addStyle }) => (
  <div className="space-y-2">
    {items.map((val, i) => (
      <div key={i} className="flex gap-2">
        <TextInput
          value={val}
          onChange={(e) =>
            setItems(items.map((x, idx) => (idx === i ? e.target.value : x)))
          }
          placeholder={`${placeholder} ${i + 1}`}
          className="flex-1"
        />
        <ReorderButtons
          index={i}
          total={items.length}
          onMove={(f, t) => setItems(moveItem(items, f, t))}
        />
        {items.length > 1 && (
          <button
            type="button"
            onClick={() => setItems(items.filter((_, idx) => idx !== i))}
            className="text-destructive"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    ))}
    <button
      type="button"
      onClick={() => setItems([...items, ""])}
      className={addBtn}
      style={addStyle}
    >
      <Plus className="w-3.5 h-3.5" /> Add
    </button>
  </div>
);

export function ServiceForm({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uuid, setUuid] = useState("");

  // ─── Basic ────────────────────────────────────────────────────────────────
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Implementation");

  // ─── SEO ──────────────────────────────────────────────────────────────────
  const [seoData, setSeoData] = useState({});
  const handleSeoChange = (field, value) =>
    setSeoData((prev) => ({ ...prev, [field]: value }));

  // ─── Section toggles ──────────────────────────────────────────────────────
  const [sections, setSections] = useState({
    hero: true,
    overview: true,
    approach: true,
    delivers: true,
    whoItsFor: true,
    whyTP: true,
    finalCta: true,
    seo: true,
  });
  const toggle = (k) => setSections((s) => ({ ...s, [k]: !s[k] }));

  // ─── HERO ─────────────────────────────────────────────────────────────────
  const [hero, setHero] = useState({
    heading: "",
    highlight: "",
    subheading: "",
    bgImageUrl: "",
    bgImageAlt: "",
  });
  const [bannerPrimary, setBannerPrimary] = useState({ text: "", url: "" });
  const [bannerSecondary, setBannerSecondary] = useState({ text: "", url: "" });
  const [bannerStats, setBannerStats] = useState([
    { icon: "Users", title: "", decp: "" },
  ]);

  // ─── OVERVIEW ─────────────────────────────────────────────────────────────
  const [overview, setOverview] = useState({
    heading: "",
    description: "",
    imageUrl: "",
  });
  const [overviewPoints, setOverviewPoints] = useState([""]);

  // ─── OUR APPROACH (Methodology) ───────────────────────────────────────────
  const [approach, setApproach] = useState({ heading: "" });
  const [approachSteps, setApproachSteps] = useState([
    { step_no: "1", title: "", desc: "" },
  ]);

  // ─── WHAT WE DELIVER ──────────────────────────────────────────────────────
  const [delivers, setDelivers] = useState({ heading: "" });
  const [deliverColumns, setDeliverColumns] = useState([
    { icon: "CheckCircle2", title: "", desc: "" },
  ]);
  // const [deliverColumns, setDeliverColumns] = useState([
  //   { title: "", desc: "" },
  // ]);

  // ─── WHO IT'S FOR ─────────────────────────────────────────────────────────
  const [whoItsFor, setWhoItsFor] = useState({ heading: "" });
  const [businessTypes, setBusinessTypes] = useState([""]);
  const [industries, setIndustries] = useState([""]);
  const [keyRoles, setKeyRoles] = useState([""]);

  // ─── WHY THYNKPROCESS ────────────────────────────────────────────────────
  const [whyTP, setWhyTP] = useState({ heading: "" });
  const [whyTPCards, setWhyTPCards] = useState([
    { icon: "ShieldCheck", text: "" },
  ]);

  // ─── FINAL CTA ────────────────────────────────────────────────────────────
  const [finalCta, setFinalCta] = useState({ heading: "", description: "" });
  const [ctaPrimary, setCtaPrimary] = useState({ text: "", url: "" });
  const [ctaSecondary, setCtaSecondary] = useState({ text: "", url: "" });

  // ─── FETCH existing data on edit ──────────────────────────────────────────
  useEffect(() => {
    if (mode === "edit" && id) {
      const fetchExisting = async () => {
        try {
          const formData = new FormData();
          formData.append("main_id", id);
          const response = await server_post_data(
            get_service_page_data,
            formData,
          );
          const d = response.data?.data ?? null;
          if (!d) return;

          // ── Basic ──────────────────────────────────────────────────────
          setTitle(d.service_name ?? "");
          setCategory(d.service_category ?? "Implementation");
          setUuid(d.uuid ?? generateUUID());

          // ── Section toggles ────────────────────────────────────────────
          setSections({
            hero: true,
            overview: d.show_overview ?? true,
            approach: d.show_our_approach ?? true,
            delivers: d.show_what_we_deliver ?? true,
            whoItsFor: d.show_who_its_for ?? true,
            whyTP: d.show_why_thynkprocess ?? true,
            finalCta: d.show_cta_section ?? true,
            seo: true,
          });

          // ── Hero ──────────────────────────────────────────────────────
          setHero({
            heading: d.banner_heading ?? "",
            highlight: d.banner_highlight_word ?? "",
            subheading: d.banner_subheading ?? "",
            bgImageUrl: d.banner_bg_image ?? "",
            bgImageAlt: d.banner_bg_image_alt ?? "",
          });
          setBannerPrimary({
            text: d.banner_primary_cta_text ?? "",
            url: d.banner_primary_cta_link ?? "",
          });
          setBannerSecondary({
            text: d.banner_secondary_cta_text ?? "",
            url: d.banner_secondary_cta_link ?? "",
          });
          setBannerStats(
            d.banner_stats?.length
              ? d.banner_stats
              : [{ icon: "Users", title: "", decp: "" }],
          );

          // ── Overview ──────────────────────────────────────────────────
          setOverview({
            heading: d.overview_heading ?? "",
            description: d.overview_description ?? "",
            imageUrl: d.overview_left_image ?? "",
          });
          setOverviewPoints(
            d.overview_points?.length ? d.overview_points : [""],
          );

          // ── Our Approach ──────────────────────────────────────────────
          setApproach({ heading: d.our_approach_heading ?? "" });
          setApproachSteps(
            d.our_approach_steps?.length
              ? d.our_approach_steps.map((s) => ({
                  step_no: s.step_no ?? "",
                  title: s.title ?? "",
                  desc: s.desc ?? "",
                }))
              : [{ step_no: "1", title: "", desc: "" }],
          );

          // ── What We Deliver ───────────────────────────────────────────
          setDelivers({ heading: d.what_we_deliver_heading ?? "" });
          setDeliverColumns(
            d.what_we_deliver_columns?.length
              ? d.what_we_deliver_columns.map((c) => ({
                  icon: c.icon ?? "CheckCircle2",
                  title: c.title ?? "",
                  desc: c.desc ?? "",
                }))
              : [{ icon: "CheckCircle2", title: "", desc: "" }],
          );
          // setDeliverColumns(
          //   d.what_we_deliver_columns?.length
          //     ? d.what_we_deliver_columns.map((c) => ({
          //         title: c.title ?? "",
          //         desc: c.desc ?? "",
          //       }))
          //     : [{ title: "", desc: "" }],
          // );

          // ── Who It's For ──────────────────────────────────────────────
          setWhoItsFor({ heading: d.who_its_for_heading ?? "" });
          const cols = d.who_its_for_columns ?? [];
          const findCol = (name) =>
            cols.find((c) => c.title === name)?.points ?? [""];
          setBusinessTypes(findCol("Business Types"));
          setIndustries(findCol("Industries"));
          setKeyRoles(findCol("Key Roles"));

          // ── Why ThynkProcess ──────────────────────────────────────────
          setWhyTP({ heading: d.why_tp_heading ?? "" });
          setWhyTPCards(
            d.why_tp_cards?.length
              ? d.why_tp_cards
              : [{ icon: "ShieldCheck", text: "" }],
          );

          // ── Final CTA ─────────────────────────────────────────────────
          setFinalCta({
            heading: d.cta_heading ?? "",
            description: d.cta_description ?? "",
          });
          setCtaPrimary({
            text: d.cta_primary_text ?? "",
            url: d.cta_primary_link ?? "",
          });
          setCtaSecondary({
            text: d.cta_secondary_text ?? "",
            url: d.cta_secondary_link ?? "",
          });

          // ── SEO ───────────────────────────────────────────────────────
          const sd = d.seo_data ?? {};
          setSeoData({
            seo_id: sd.seo_id ?? 0,
            title_name: sd.title_name ?? "",
            description: sd.description ?? "",
            image: sd.image ?? "",
            actual_url: sd.actual_url ?? "",
            link_url: sd.link_url ?? "",
            favicon: sd.favicon ?? "",
            keywords: sd.keywords ?? "",
            changefreq: sd.changefreq ?? "",
            priority_screen: sd.priority ?? "0.8",
          });
        } catch (err) {
          console.error("Failed to load service:", err);
          showError("Failed to load service data. Please try again.");
        }
      };
      fetchExisting();
    }
  }, [mode, id]);

  // ─── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!title.trim()) e.title = "Title is required";
    return e;
  };

  // ─── BUILD PAYLOAD ────────────────────────────────────────────────────────
  const buildPayload = (saveStatus) => {
    const uuId = mode === "edit" ? uuid || generateUUID() : generateUUID();
    return {
      p_id: mode === "edit" ? Number(id) : 0,
      uuid: uuId,
      status: saveStatus,
      service_name: title,
      service_category: category,

      banner_heading: hero.heading,
      banner_highlight_word: hero.highlight,
      banner_subheading: hero.subheading,
      banner_bg_image: hero.bgImageUrl,
      banner_bg_image_alt: hero.bgImageAlt,
      banner_primary_cta_text: bannerPrimary.text,
      banner_primary_cta_link: bannerPrimary.url,
      banner_secondary_cta_text: bannerSecondary.text,
      banner_secondary_cta_link: bannerSecondary.url,
      banner_stats: bannerStats.filter((s) => s.title || s.decp),

      show_overview: sections.overview,
      overview_heading: overview.heading,
      overview_description: overview.description,
      // overview_points: overviewPoints.filter(Boolean),
      overview_points: overviewPoints
        .filter(Boolean)
        .map((point) => ({ title: "", decp: point })),
      overview_left_image: overview.imageUrl,

      show_our_approach: sections.approach,
      our_approach_heading: approach.heading,
      our_approach_steps: approachSteps.map((s, i) => ({
        step_no: String(i + 1),
        title: s.title,
        desc: s.desc,
      })),

      show_what_we_deliver: sections.delivers,
      what_we_deliver_heading: delivers.heading,
      what_we_deliver_columns: deliverColumns.map((c) => ({
        icon: c.icon,
        title: c.title,
        desc: c.desc,
      })),
      // what_we_deliver_columns: deliverColumns.map((c) => ({
      //   title: c.title,
      //   desc: c.desc,
      // })),

      show_who_its_for: sections.whoItsFor,
      who_its_for_heading: whoItsFor.heading,
      who_its_for_columns: [
        { title: "Business Types", points: businessTypes.filter(Boolean) },
        { title: "Industries", points: industries.filter(Boolean) },
        { title: "Key Roles", points: keyRoles.filter(Boolean) },
      ],

      show_why_thynkprocess: sections.whyTP,
      why_tp_heading: whyTP.heading,
      why_tp_cards: whyTPCards.filter((c) => c.text),

      show_cta_section: sections.finalCta,
      cta_heading: finalCta.heading,
      cta_description: finalCta.description,
      cta_primary_text: ctaPrimary.text,
      cta_primary_link: ctaPrimary.url,
      cta_secondary_text: ctaSecondary.text,
      cta_secondary_link: ctaSecondary.url,

      title_name: seoData.title_name ?? "",
      description: seoData.description ?? "",
      image: seoData.image ?? "",
      favicon: seoData.favicon ?? "",
      actual_url: seoData.actual_url ?? "",
      link_url: seoData.link_url ?? "",
      keywords: seoData.keywords ?? "",
      changefreq: seoData.changefreq ?? "",
      priority: seoData.priority_screen ?? "0.8",
    };
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async (saveStatus) => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      showError("Please fix the errors");
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const payload = buildPayload(saveStatus);
      console.log("=== ServiceForm Payload ===", payload);

      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (typeof value === "boolean") {
          formData.append(key, value ? 1 : 0);
        } else if (typeof value === "object") {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      });

      const saveRes = await server_post_data(service_page_save_update, payload);
      showSuccess(mode === "create" ? "Service created!" : "Service updated!");
      showSeoWarnings(saveRes);
      navigate("/services");
    } catch {
      showError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Shared style helpers ─────────────────────────────────────────────────
  const addBtn =
    "flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg";
  const addStyle = {
    background: "hsl(var(--accent-muted))",
    color: "hsl(var(--accent))",
  };
  const cardCls = "p-3 rounded-lg border border-border space-y-3 mb-2";

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[900px] mx-auto space-y-5">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/services")}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">
              {mode === "create" ? "Add Service" : "Edit Service"}
            </h1>
            <p className="page-subtitle">Service detail page editor</p>
          </div>
        </div>
      </div>

      {/* ── Basic Info ── */}
      <div className="section-panel">
        <div className="section-panel-header">
          <h2 className="section-panel-title">Basic Info</h2>
        </div>
        <div className="section-panel-body space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Service Title" required error={errors.title}>
              <TextInput
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="SAP B1 Implementation"
                error={!!errors.title}
              />
            </Field>
            <Field label="Category">
              <SelectInput
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                options={CATEGORIES}
              />
            </Field>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          1. HERO
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Hero Section"
        active={sections.hero}
        onToggleActive={() => toggle("hero")}
      >
        <Field label="Main Heading">
          <TextInput
            value={hero.heading}
            onChange={(e) =>
              setHero((h) => ({ ...h, heading: e.target.value }))
            }
            placeholder="SAP B1 Implementation"
          />
        </Field>
        <Field label="Highlight Word">
          <TextInput
            value={hero.highlight}
            onChange={(e) =>
              setHero((h) => ({ ...h, highlight: e.target.value }))
            }
            placeholder="Digital"
          />
        </Field>
        <Field label="Subheading">
          <TextArea
            value={hero.subheading}
            onChange={(e) =>
              setHero((h) => ({ ...h, subheading: e.target.value }))
            }
            placeholder="Supporting description"
          />
        </Field>
        <Field label="Background Image">
          <MediaImageField
            label="Select background image"
            hint="Recommended: 1920×1080px"
            value={hero.bgImageUrl}
            onChange={(v) => setHero((h) => ({ ...h, bgImageUrl: v }))}
            alt={hero.bgImageAlt}
            onAltChange={(v) => setHero((h) => ({ ...h, bgImageAlt: v }))}
          />
        </Field>

        <SectionDivider title="Buttons" />
        <CTAGroup
          label="Primary Button"
          text={bannerPrimary.text}
          url={bannerPrimary.url}
          onTextChange={(v) => setBannerPrimary((b) => ({ ...b, text: v }))}
          onUrlChange={(v) => setBannerPrimary((b) => ({ ...b, url: v }))}
        />
        <CTAGroup
          label="Secondary Button"
          text={bannerSecondary.text}
          url={bannerSecondary.url}
          onTextChange={(v) => setBannerSecondary((b) => ({ ...b, text: v }))}
          onUrlChange={(v) => setBannerSecondary((b) => ({ ...b, url: v }))}
        />

        <SectionDivider title="Stats Row" />
        <p className="text-xs text-muted-foreground mb-3">
          Icon · Title (e.g. "Clients") · Value (e.g. "500+")
        </p>
        {bannerStats.map((st, i) => (
          <div key={i} className={cardCls}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground">
                Stat {i + 1}
              </p>
              <div className="flex items-center gap-2">
                <ReorderButtons
                  index={i}
                  total={bannerStats.length}
                  onMove={(f, t) => setBannerStats(moveItem(bannerStats, f, t))}
                />
                {bannerStats.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      setBannerStats(bannerStats.filter((_, idx) => idx !== i))
                    }
                    className="text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <Field label="Icon">
              <SelectInput
                value={st.icon}
                onChange={(e) =>
                  setBannerStats(
                    bannerStats.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
                options={ICON_OPTIONS}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Title">
                <TextInput
                  value={st.title}
                  onChange={(e) =>
                    setBannerStats(
                      bannerStats.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Clients"
                />
              </Field>
              <Field label="Value">
                <TextInput
                  value={st.decp}
                  onChange={(e) =>
                    setBannerStats(
                      bannerStats.map((x, idx) =>
                        idx === i ? { ...x, decp: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="500+"
                />
              </Field>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setBannerStats([
              ...bannerStats,
              { icon: "Users", title: "", decp: "" },
            ])
          }
          className={addBtn}
          style={addStyle}
        >
          <Plus className="w-3.5 h-3.5" /> Add Stat
        </button>
      </CollapsibleSection>

      {/* ══════════════════════════════════════════════
          2. OVERVIEW
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Overview Section"
        active={sections.overview}
        onToggleActive={() => toggle("overview")}
      >
        <Field label="Heading">
          <TextInput
            value={overview.heading}
            onChange={(e) =>
              setOverview((o) => ({ ...o, heading: e.target.value }))
            }
            placeholder="Why SAP B1 Implementation Matters"
          />
        </Field>
        <Field label="Description">
          <TextArea
            rows={4}
            value={overview.description}
            onChange={(e) =>
              setOverview((o) => ({ ...o, description: e.target.value }))
            }
          />
        </Field>
        <Field label="Section Image">
          <MediaImageField
            label="Upload section image"
            hint="800×500px"
            value={overview.imageUrl}
            onChange={(v) => setOverview((o) => ({ ...o, imageUrl: v }))}
          />
        </Field>
        <SectionDivider title="What's Included" />
        <StringRepeater
          items={overviewPoints}
          setItems={setOverviewPoints}
          placeholder="Point"
          addBtn={addBtn}
          addStyle={addStyle}
        />
      </CollapsibleSection>

      {/* ══════════════════════════════════════════════
          3. OUR APPROACH
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Our Approach"
        active={sections.approach}
        onToggleActive={() => toggle("approach")}
      >
        <Field label="Heading">
          <TextInput
            value={approach.heading}
            onChange={(e) =>
              setApproach((a) => ({ ...a, heading: e.target.value }))
            }
            placeholder="Our Implementation Methodology"
          />
        </Field>
        <SectionDivider title="Steps" />
        {approachSteps.map((step, i) => (
          <div
            key={i}
            className="flex gap-3 p-3 rounded-lg border border-border mb-2"
          >
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
              style={{ background: "hsl(var(--accent))" }}
            >
              {String(i + 1).padStart(2, "0")}
            </div>
            <div className="flex-1 space-y-2">
              <Field label="Step Title">
                <TextInput
                  value={step.title}
                  onChange={(e) =>
                    setApproachSteps(
                      approachSteps.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Discovery & Planning"
                />
              </Field>
              <Field label="Description">
                <TextArea
                  rows={2}
                  value={step.desc}
                  onChange={(e) =>
                    setApproachSteps(
                      approachSteps.map((x, idx) =>
                        idx === i ? { ...x, desc: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
            <div className="flex flex-col gap-1">
              <ReorderButtons
                index={i}
                total={approachSteps.length}
                onMove={(f, t) =>
                  setApproachSteps(moveItem(approachSteps, f, t))
                }
              />
              {approachSteps.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setApproachSteps(
                      approachSteps.filter((_, idx) => idx !== i),
                    )
                  }
                  className="text-destructive mt-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setApproachSteps([
              ...approachSteps,
              {
                step_no: String(approachSteps.length + 1),
                title: "",
                desc: "",
              },
            ])
          }
          className={addBtn}
          style={addStyle}
        >
          <Plus className="w-3.5 h-3.5" /> Add Step
        </button>
      </CollapsibleSection>

      {/* ══════════════════════════════════════════════
          4. WHAT WE DELIVER
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="What We Deliver"
        active={sections.delivers}
        onToggleActive={() => toggle("delivers")}
      >
        <Field label="Heading">
          <TextInput
            value={delivers.heading}
            onChange={(e) =>
              setDelivers((d) => ({ ...d, heading: e.target.value }))
            }
            placeholder="Deliverables You Can Expect"
          />
        </Field>
        <SectionDivider title="Deliverable Cards" />
        {deliverColumns.map((col, i) => (
          <div key={i} className={cardCls}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground">
                Card {i + 1}
              </p>
              <div className="flex items-center gap-2">
                <ReorderButtons
                  index={i}
                  total={deliverColumns.length}
                  onMove={(f, t) =>
                    setDeliverColumns(moveItem(deliverColumns, f, t))
                  }
                />
                {deliverColumns.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      setDeliverColumns(
                        deliverColumns.filter((_, idx) => idx !== i),
                      )
                    }
                    className="text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Title">
                <TextInput
                  value={col.title}
                  onChange={(e) =>
                    setDeliverColumns(
                      deliverColumns.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Configured SAP B1 System"
                />
              </Field>
              <Field label="Icon">
                <SelectInput
                  value={col.icon}
                  onChange={(e) =>
                    setDeliverColumns(
                      deliverColumns.map((x, idx) =>
                        idx === i ? { ...x, icon: e.target.value } : x,
                      ),
                    )
                  }
                  options={ICON_OPTIONS}
                />
              </Field>
            </div>
            <div className="grid grid-cols-1">
              <Field label="Description">
                <TextInput
                  value={col.desc}
                  onChange={(e) =>
                    setDeliverColumns(
                      deliverColumns.map((x, idx) =>
                        idx === i ? { ...x, desc: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Brief description"
                />
              </Field>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setDeliverColumns([...deliverColumns, { title: "", desc: "" }])
          }
          className={addBtn}
          style={addStyle}
        >
          <Plus className="w-3.5 h-3.5" /> Add Card
        </button>
      </CollapsibleSection>

      {/* ══════════════════════════════════════════════
          5. IS THIS RIGHT FOR YOU
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Is This Service Right For You?"
        active={sections.whoItsFor}
        onToggleActive={() => toggle("whoItsFor")}
      >
        <Field label="Heading">
          <TextInput
            value={whoItsFor.heading}
            onChange={(e) =>
              setWhoItsFor((w) => ({ ...w, heading: e.target.value }))
            }
            placeholder="Is This Service Right For You?"
          />
        </Field>
        <SectionDivider title="Business Types" />
        <StringRepeater
          items={businessTypes}
          setItems={setBusinessTypes}
          placeholder="Business type"
          addBtn={addBtn}
          addStyle={addStyle}
        />
        <SectionDivider title="Industries" />
        <StringRepeater
          items={industries}
          setItems={setIndustries}
          placeholder="Industry"
          addBtn={addBtn}
          addStyle={addStyle}
        />
        <SectionDivider title="Key Roles" />
        <StringRepeater
          items={keyRoles}
          setItems={setKeyRoles}
          placeholder="Role"
          addBtn={addBtn}
          addStyle={addStyle}
        />
      </CollapsibleSection>

      {/* ══════════════════════════════════════════════
          6. WHY THYNKPROCESS
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Why ThynkProcess?"
        active={sections.whyTP}
        onToggleActive={() => toggle("whyTP")}
      >
        <Field label="Heading">
          <TextInput
            value={whyTP.heading}
            onChange={(e) =>
              setWhyTP((w) => ({ ...w, heading: e.target.value }))
            }
            placeholder="Why Choose ThynkProcess?"
          />
        </Field>
        <SectionDivider title="Cards" />
        {whyTPCards.map((card, i) => (
          <div key={i} className={cardCls}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground">
                Card {i + 1}
              </p>
              <div className="flex items-center gap-2">
                <ReorderButtons
                  index={i}
                  total={whyTPCards.length}
                  onMove={(f, t) => setWhyTPCards(moveItem(whyTPCards, f, t))}
                />
                {whyTPCards.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      setWhyTPCards(whyTPCards.filter((_, idx) => idx !== i))
                    }
                    className="text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Icon">
                <SelectInput
                  value={card.icon}
                  onChange={(e) =>
                    setWhyTPCards(
                      whyTPCards.map((x, idx) =>
                        idx === i ? { ...x, icon: e.target.value } : x,
                      ),
                    )
                  }
                  options={ICON_OPTIONS}
                />
              </Field>
              <Field label="Text">
                <TextInput
                  value={card.text}
                  onChange={(e) =>
                    setWhyTPCards(
                      whyTPCards.map((x, idx) =>
                        idx === i ? { ...x, text: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Certified SAP Partners"
                />
              </Field>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setWhyTPCards([...whyTPCards, { icon: "ShieldCheck", text: "" }])
          }
          className={addBtn}
          style={addStyle}
        >
          <Plus className="w-3.5 h-3.5" /> Add Card
        </button>
      </CollapsibleSection>

      {/* ══════════════════════════════════════════════
          7. FINAL CTA
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Final CTA Section"
        active={sections.finalCta}
        onToggleActive={() => toggle("finalCta")}
      >
        <Field label="Heading">
          <TextInput
            value={finalCta.heading}
            onChange={(e) =>
              setFinalCta((c) => ({ ...c, heading: e.target.value }))
            }
            placeholder="Ready to Transform?"
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={finalCta.description}
            onChange={(e) =>
              setFinalCta((c) => ({ ...c, description: e.target.value }))
            }
          />
        </Field>
        <CTAGroup
          label="Primary Button"
          text={ctaPrimary.text}
          url={ctaPrimary.url}
          onTextChange={(v) => setCtaPrimary((b) => ({ ...b, text: v }))}
          onUrlChange={(v) => setCtaPrimary((b) => ({ ...b, url: v }))}
        />
        <CTAGroup
          label="Secondary Button"
          text={ctaSecondary.text}
          url={ctaSecondary.url}
          onTextChange={(v) => setCtaSecondary((b) => ({ ...b, text: v }))}
          onUrlChange={(v) => setCtaSecondary((b) => ({ ...b, url: v }))}
        />
      </CollapsibleSection>

      {/* ══════════════════════════════════════════════
          8. SEO — same as ProductForm
      ══════════════════════════════════════════════ */}
      <SEOSection seoData={seoData} onChange={handleSeoChange} />

      <FormActions
        onCancel={() => navigate("/services")}
        onSaveDraft={() => handleSave("draft")}
        onPublish={() => handleSave("published")}
        loading={loading}
      />
    </div>
  );
}
